import { useState, useCallback, useLayoutEffect, useEffect, useRef } from 'react';

/**
 * Sliding navy pill behind the active vitals tab — same motion as practice dashboard tabs.
 */
export function useVitalsTabIndicator(activeTab, tabCount = 0) {
  const tabsWrapRef = useRef(null);
  const [tabIndicatorStyle, setTabIndicatorStyle] = useState({ opacity: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);

  const updateTabIndicator = useCallback(() => {
    const wrap = tabsWrapRef.current;
    if (!wrap) return;

    const activeButton = wrap.querySelector('.res-vitals-tab.is-active');
    if (!activeButton) {
      setTabIndicatorStyle({ opacity: 0 });
      return;
    }

    const wrapRect = wrap.getBoundingClientRect();
    const btnRect = activeButton.getBoundingClientRect();

    setTabIndicatorStyle({
      width: `${btnRect.width}px`,
      height: `${btnRect.height}px`,
      transform: `translate(${btnRect.left - wrapRect.left}px, ${btnRect.top - wrapRect.top}px)`,
      opacity: 1
    });
    setIndicatorReady(true);
  }, []);

  useLayoutEffect(() => {
    updateTabIndicator();
  }, [activeTab, tabCount, updateTabIndicator]);

  useEffect(() => {
    const wrap = tabsWrapRef.current;
    if (!wrap) return undefined;

    const tabsList = wrap.querySelector('.res-vitals-tabs');
    const resizeObserver = new ResizeObserver(updateTabIndicator);

    resizeObserver.observe(wrap);
    if (tabsList) {
      resizeObserver.observe(tabsList);
      tabsList.addEventListener('scroll', updateTabIndicator, { passive: true });
    }
    window.addEventListener('resize', updateTabIndicator);

    return () => {
      resizeObserver.disconnect();
      if (tabsList) {
        tabsList.removeEventListener('scroll', updateTabIndicator);
      }
      window.removeEventListener('resize', updateTabIndicator);
    };
  }, [updateTabIndicator]);

  return {
    tabsWrapRef,
    tabIndicatorStyle,
    indicatorReady
  };
}
